import type { ColumnDef } from "@tanstack/react-table";

import type { Action } from "@/components/MenuButton";
import { buildActionColumn, buildTagColumn } from "@/components/Table";
import type { TagType } from "@/components/Tag/Tag";
import { actionId } from "@/hooks/useTableRowMenu";

import { UNTITLED_CAREER_PLAN_TITLE } from "./_CareerPlanTitle";
import type { CareerPlanStatusDialogMode } from "./_CareerPlanStatusDialog";
import { CAREER_PLAN_STATUS, type CareerPlanStatus } from "./careerPlanDocument";
import { getCareerPlanTableActions } from "./careerPlanStatusActions";
import type { HubCareerPlan } from "./careerPlanHubSummaries";

export type CareerPlanListRow = HubCareerPlan & {
  displayTitle: string;
  lastModified: string;
};

export function planDisplayTitle(title: string | null) {
  return title?.trim() || UNTITLED_CAREER_PLAN_TITLE;
}

function planStatusLabel(status: string) {
  switch (status) {
    case CAREER_PLAN_STATUS.UNPUBLISHED:
      return "Unpublished";
    case CAREER_PLAN_STATUS.PUBLISHED:
      return "Published";
    case CAREER_PLAN_STATUS.COMPLETED:
      return "Completed";
    case CAREER_PLAN_STATUS.DRAFT:
      return "Draft";
    default:
      return "Draft";
  }
}

function planStatusTagType(status: string): TagType {
  switch (status) {
    case CAREER_PLAN_STATUS.UNPUBLISHED:
      return "warn";
    case CAREER_PLAN_STATUS.PUBLISHED:
      return "success";
    case CAREER_PLAN_STATUS.COMPLETED:
      return "information";
    case CAREER_PLAN_STATUS.DRAFT:
      return "surface";
    default:
      return "surface";
  }
}

function planStatusSortRank(status: string): number {
  switch (status) {
    case CAREER_PLAN_STATUS.PUBLISHED:
      return 0;
    case CAREER_PLAN_STATUS.UNPUBLISHED:
      return 1;
    case CAREER_PLAN_STATUS.DRAFT:
      return 2;
    case CAREER_PLAN_STATUS.COMPLETED:
      return 3;
    default:
      return 2;
  }
}

interface ColumnsOptions {
  setMenu: (opts: { rowId: string; actions: Action[] }) => void;
  canManage: boolean;
  downloadingPlanId?: string | null;
  onAction: (plan: CareerPlanListRow, mode: CareerPlanStatusDialogMode) => void;
  onDownloadPdf: (plan: CareerPlanListRow) => void;
}

export function getColumns({
  setMenu,
  canManage,
  downloadingPlanId,
  onAction,
  onDownloadPdf
}: ColumnsOptions): ColumnDef<CareerPlanListRow, unknown>[] {
  return [
    {
      accessorKey: "displayTitle",
      header: "Title",
      size: 360,
      enableSorting: false,
      meta: { headerClassName: "hide-header-in-column" }
    },
    {
      accessorKey: "updatedAt",
      header: "Last Modified",
      size: 160,
      meta: { sortType: "date" },
      cell: ({ row }) => row.original.lastModified
    },
    {
      ...buildTagColumn<CareerPlanListRow>({
        accessorKey: "status",
        header: "Status",
        size: 160,
        enableSorting: true,
        sortType: "text",
        getTagLabel: (row) => planStatusLabel(row.status),
        getTagType: (row) => planStatusTagType(row.status)
      }),
      sortingFn: (rowA, rowB) => planStatusSortRank(rowA.original.status) - planStatusSortRank(rowB.original.status)
    },
    buildActionColumn<CareerPlanListRow>({
      getActions: (row) =>
        getCareerPlanTableActions(
          row.status as CareerPlanStatus,
          {
            downloadPdf: () => onDownloadPdf(row),
            publish: () => onAction(row, "publish"),
            unpublish: () => onAction(row, "unpublish"),
            complete: () => onAction(row, "complete"),
            delete: () => onAction(row, "delete")
          },
          { canManage, downloading: downloadingPlanId === row.id }
        ),
      actionId,
      setMenu,
      getRowId: (row) => row.id
    })
  ];
}
